import React, { useMemo } from 'react';

const Summary = ({ summary, transactions, subscriptions, salaryPayments, balances, exchangeRate, monthlySummary, formatCurrency }) => {
  // Calculate advanced metrics
  const metrics = useMemo(() => {
    const totalIncome = summary.totalIncome || 0;
    const totalExpenses = summary.totalExpenses || 0;
    const totalLoans = summary.totalLoans || 0;
    const netSavings = totalIncome - totalExpenses;

    // Financial Health Score (0-100)
    const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;
    const expenseRatio = totalIncome > 0 ? (totalExpenses / totalIncome) * 100 : 0;
    const loanRatio = totalIncome > 0 ? (Math.abs(totalLoans) / totalIncome) * 100 : 0;

    let healthScore = 100;
    if (savingsRate < 0) healthScore -= 30; // Negative savings
    else if (savingsRate < 10) healthScore -= 20; // Low savings
    else if (savingsRate > 30) healthScore += 0; // Good savings

    if (expenseRatio > 90) healthScore -= 20; // Very high expenses
    else if (expenseRatio > 70) healthScore -= 10; // High expenses

    if (loanRatio > 50) healthScore -= 15; // High debt
    else if (loanRatio > 30) healthScore -= 10; // Moderate debt

    healthScore = Math.max(0, Math.min(100, healthScore));

    // Burn rate (months of runway)
    const currentBalance = (balances.alfalah || 0) + (balances.meezan || 0) + ((balances.payoneer || 0) * (exchangeRate || 365));
    const avgMonthlyExpenses = totalExpenses; // Simplified - could calculate from historical data
    const burnRate = avgMonthlyExpenses > 0 ? currentBalance / avgMonthlyExpenses : 0;

    // Upcoming obligations (next 30 days)
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

    const upcomingSubscriptions = subscriptions.filter(sub => {
      const dueDay = sub.dueDay;
      const dueDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
      if (dueDate < today) {
        dueDate.setMonth(dueDate.getMonth() + 1);
      }
      return dueDate <= nextMonth && sub.lastPaidMonth !== `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    });

    const upcomingSubsTotal = upcomingSubscriptions.reduce((sum, sub) => sum + (Number(sub.amount) || 0), 0);

    // Upcoming salaries (assuming monthly)
    const unpaidSalaries = salaryPayments.filter(payment => {
      const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      return payment.month === currentMonth && payment.status !== 'paid';
    });

    const upcomingSalariesTotal = unpaidSalaries.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);

    // MoM Growth
    const currentMonth = monthlySummary[monthlySummary.length - 1];
    const prevMonth = monthlySummary[monthlySummary.length - 2];
    const incomeGrowth = prevMonth && prevMonth.income > 0 ? ((currentMonth.income - prevMonth.income) / prevMonth.income) * 100 : 0;
    const expenseGrowth = prevMonth && prevMonth.expenses > 0 ? ((currentMonth.expenses - prevMonth.expenses) / prevMonth.expenses) * 100 : 0;

    return {
      totalIncome,
      totalExpenses,
      totalLoans,
      netSavings,
      savingsRate,
      expenseRatio,
      healthScore,
      burnRate,
      currentBalance,
      incomeGrowth,
      expenseGrowth,
      upcomingObligations: upcomingSubsTotal + upcomingSalariesTotal,
      upcomingSubscriptions: upcomingSubsTotal,
      upcomingSalaries: upcomingSalariesTotal
    };
  }, [summary, subscriptions, salaryPayments, balances, exchangeRate, monthlySummary]);

  // Get health score color and label
  const getHealthStatus = (score) => {
    if (score >= 80) return { color: '#10b981', label: 'Excellent', icon: '🌟' };
    if (score >= 60) return { color: '#3b82f6', label: 'Good', icon: '👍' };
    if (score >= 40) return { color: '#f59e0b', label: 'Fair', icon: '⚠️' };
    return { color: '#ef4444', label: 'Poor', icon: '⚠️' };
  };

  const healthStatus = getHealthStatus(metrics.healthScore);

  return (
    <div className="fade-in">
      <h2 className="text-2xl font-bold mb-6 text-yellow">📊 Financial Summary</h2>

      {/* Financial Health Score */}
      <div className="card mb-6" style={{ background: 'linear-gradient(135deg, rgba(255,255,0,0.1) 0%, rgba(16,185,129,0.1) 100%)' }}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold mb-1">Financial Health Score</h3>
            <p className="text-sm text-muted">Overall financial wellness indicator</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold" style={{ color: healthStatus.color }}>
              {metrics.healthScore.toFixed(0)}
            </div>
            <div className="text-sm font-semibold" style={{ color: healthStatus.color }}>
              {healthStatus.icon} {healthStatus.label}
            </div>
          </div>
        </div>
        <div style={{
          width: '100%',
          height: '12px',
          backgroundColor: 'var(--secondary-bg)',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${metrics.healthScore}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${healthStatus.color} 0%, ${healthStatus.color}dd 100%)`,
            transition: 'width 0.5s ease'
          }}></div>
        </div>
      </div>

      {/* Quick Insights Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="stat-card success">
          <p className="text-xs text-gray mb-1">💰 Total Income</p>
          <p className="text-xl font-bold text-green">{formatCurrency(metrics.totalIncome)}</p>
          <p className={`text-xs mt-1 ${metrics.incomeGrowth >= 0 ? 'text-green' : 'text-red'}`}>
            {metrics.incomeGrowth >= 0 ? '↑' : '↓'} {Math.abs(metrics.incomeGrowth).toFixed(1)}% MoM
          </p>
        </div>

        <div className="stat-card" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <p className="text-xs text-gray mb-1">💸 Total Expenses</p>
          <p className="text-xl font-bold text-red">{formatCurrency(metrics.totalExpenses)}</p>
          <p className={`text-xs mt-1 ${metrics.expenseGrowth <= 0 ? 'text-green' : 'text-red'}`}>
            {metrics.expenseGrowth >= 0 ? '↑' : '↓'} {Math.abs(metrics.expenseGrowth).toFixed(1)}% MoM
          </p>
        </div>

        <div className="stat-card" style={{ background: metrics.netSavings >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${metrics.netSavings >= 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
          <p className="text-xs text-gray mb-1">💵 Net Savings</p>
          <p className={`text-xl font-bold ${metrics.netSavings >= 0 ? 'text-green' : 'text-red'}`}>
            {formatCurrency(metrics.netSavings)}
          </p>
          <p className="text-xs text-muted mt-1">{metrics.savingsRate.toFixed(1)}% rate</p>
        </div>

        <div className="stat-card warning">
          <p className="text-xs text-gray mb-1">🔥 Burn Rate</p>
          <p className="text-xl font-bold text-orange">
            {metrics.burnRate > 0 ? `${metrics.burnRate.toFixed(1)}mo` : 'N/A'}
          </p>
          <p className="text-xs text-muted mt-1">Months of runway</p>
        </div>
      </div>

      {/* Upcoming Obligations */}
      {metrics.upcomingObligations > 0 && (
        <div className="card mb-6" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
          <h3 className="text-lg font-bold mb-3">⏰ Upcoming Obligations (Next 30 Days)</h3>
          <div className="space-y-2">
            {metrics.upcomingSubscriptions > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm">💳 Subscriptions</span>
                <span className="font-bold text-orange">{formatCurrency(metrics.upcomingSubscriptions)}</span>
              </div>
            )}
            {metrics.upcomingSalaries > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm">👥 Salaries</span>
                <span className="font-bold text-orange">{formatCurrency(metrics.upcomingSalaries)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-gray-300">
              <span className="font-semibold">Total</span>
              <span className="text-lg font-bold text-orange">{formatCurrency(metrics.upcomingObligations)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card">
          <h4 className="text-sm font-semibold mb-2 text-gray">Current Balance</h4>
          <p className="text-2xl font-bold text-blue">{formatCurrency(metrics.currentBalance)}</p>
          <p className="text-xs text-muted mt-1">Across all accounts</p>
        </div>

        <div className="card">
          <h4 className="text-sm font-semibold mb-2 text-gray">Outstanding Loans</h4>
          <p className="text-2xl font-bold text-orange">{formatCurrency(metrics.totalLoans)}</p>
          <p className="text-xs text-muted mt-1">Founder loans</p>
        </div>
      </div>

      {/* Founder Loans Breakdown */}
      {summary.loansByPartner && summary.loansByPartner.length > 0 && (
        <div className="card mb-6">
          <h3 className="text-xl font-semibold mb-4">💳 Founder Loan Breakdown</h3>
          <div className="space-y-3">
            {summary.loansByPartner.map((partner, index) => {
              const percentage = metrics.totalLoans > 0 ? (Math.abs(partner.total) / metrics.totalLoans * 100) : 0;
              return (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold">{partner.name}</span>
                    <span className={`font-bold ${partner.total > 0 ? 'text-orange' : partner.total < 0 ? 'text-green' : 'text-gray'}`}>
                      {formatCurrency(partner.total)}
                      {metrics.totalLoans > 0 && ` (${percentage.toFixed(1)}%)`}
                    </span>
                  </div>
                  {metrics.totalLoans > 0 && (
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: 'var(--secondary-bg)',
                      borderRadius: '4px'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: partner.total > 0 ? '#f59e0b' : '#10b981',
                        borderRadius: '4px'
                      }}></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expense Categories Breakdown */}
      {summary.expensesByCategory && summary.expensesByCategory.length > 0 && (
        <div className="card mb-6">
          <h3 className="text-xl font-semibold mb-4">📋 Expense Categories</h3>
          <div className="space-y-3">
            {[...summary.expensesByCategory]
              .sort((a, b) => b.total - a.total)
              .slice(0, 10) // Top 10
              .map((category, index) => {
                const percentage = metrics.totalExpenses > 0 ? (category.total / metrics.totalExpenses * 100) : 0;
                return (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold">{category.category || 'Uncategorized'}</span>
                      <span className="text-red font-bold">
                        {formatCurrency(category.total)} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: 'var(--secondary-bg)',
                      borderRadius: '4px'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: '#ef4444',
                        borderRadius: '4px'
                      }}></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Reconciliation Section */}
      {summary.reconciliation && (
        <div className="card mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}>
          <h3 className="text-xl font-bold mb-4 text-yellow">⚖️ Balance Reconciliation</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted">Net Profit (Lifetime)</span>
              <span className="font-bold">{formatCurrency(summary.reconciliation.expectedBalance)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted">Actual Bank Balance (Current)</span>
              <span className="font-bold text-blue">{formatCurrency(summary.reconciliation.actualBalance)}</span>
            </div>
            <div className="pt-2 border-t border-border flex justify-between items-center">
              <span className="font-bold">Total Difference</span>
              <span className={`text-lg font-bold ${summary.reconciliation.discrepancy >= 0 ? 'text-green' : 'text-red'}`}>
                {summary.reconciliation.discrepancy >= 0 ? '+' : ''}{formatCurrency(summary.reconciliation.discrepancy)}
              </span>
            </div>
            <p className="text-xs text-muted mt-4 bg-secondary p-3 rounded italic">
              * This difference is caused by currency exchange rate fluctuations and starting balances.
              Pounds held in Payoneer are re-valued at the current market rate ({exchangeRate} PKR),
              which may differ from the rate at which they were originally recorded.
            </p>
          </div>
        </div>
      )}

      {/* Insights & Recommendations */}
      <div className="card" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
        <h3 className="text-xl font-semibold mb-4">💡 Insights & Recommendations</h3>
        <ul className="space-y-2 text-sm">
          {metrics.netSavings > 0 ? (
            <li className="flex items-start gap-2">
              <span>✅</span>
              <span>Great! You have positive net savings of <strong>{formatCurrency(metrics.netSavings)}</strong> ({metrics.savingsRate.toFixed(1)}% savings rate)</span>
            </li>
          ) : (
            <li className="flex items-start gap-2">
              <span>⚠️</span>
              <span>Your expenses exceed income by <strong>{formatCurrency(Math.abs(metrics.netSavings))}</strong>. Consider reducing costs or increasing revenue.</span>
            </li>
          )}

          {metrics.burnRate > 0 && metrics.burnRate < 3 && (
            <li className="flex items-start gap-2">
              <span>⚠️</span>
              <span>Low runway: Only <strong>{metrics.burnRate.toFixed(1)} months</strong> of expenses covered. Consider building reserves.</span>
            </li>
          )}

          {metrics.burnRate >= 6 && (
            <li className="flex items-start gap-2">
              <span>✅</span>
              <span>Healthy runway: <strong>{metrics.burnRate.toFixed(1)} months</strong> of expenses covered.</span>
            </li>
          )}

          {metrics.totalLoans > 0 && (
            <li className="flex items-start gap-2">
              <span>💡</span>
              <span>Outstanding founder loans: <strong>{formatCurrency(metrics.totalLoans)}</strong>. Consider creating a repayment plan.</span>
            </li>
          )}

          {metrics.expenseRatio > 80 && (
            <li className="flex items-start gap-2">
              <span>⚠️</span>
              <span>High expense ratio: <strong>{metrics.expenseRatio.toFixed(1)}%</strong> of income. Look for optimization opportunities.</span>
            </li>
          )}

          {metrics.upcomingObligations > metrics.currentBalance && (
            <li className="flex items-start gap-2">
              <span>🚨</span>
              <span>Upcoming obligations (<strong>{formatCurrency(metrics.upcomingObligations)}</strong>) exceed current balance. Plan accordingly.</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Summary;